// src/pages/Dashboard/Admin/DetailsAdherents/components/IdentityPanel.jsx

import React, { useMemo } from "react";
import {
  FaBirthdayCake,
  FaCamera,
  FaEnvelope,
  FaHome,
  FaIdCard,
  FaInfoCircle,
  FaPhoneAlt,
  FaUser,
  FaWeight,
} from "react-icons/fa";

import {
  computeAge,
  formatDate,
  formatDateTime,
} from "../utils/adherentDashboardUtils";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function hasValue(value) {
  return value !== undefined && value !== null && String(value).trim() !== "";
}

function valueOrDash(value) {
  return hasValue(value) ? value : "—";
}

function formatWeight(value) {
  return hasValue(value) ? `${value} kg` : "—";
}

function formatAddress(adherent) {
  return [adherent?.adresse, adherent?.cp, adherent?.commune]
    .filter(Boolean)
    .join(" ");
}

function getFullName(adherent) {
  return [adherent?.prenom, adherent?.nom].filter(Boolean).join(" ") || "—";
}

function BooleanBadge({ value }) {
  const active = Boolean(value);

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold ring-1",
        active
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
          : "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20",
      )}
    >
      {active ? "Oui" : "Non"}
    </span>
  );
}

function CompactItem({ icon: Icon, label, value, children }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950">
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500 dark:bg-slate-900 dark:text-slate-300">
        <Icon className="text-sm" />
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">
          {label}
        </p>

        <div className="mt-1 text-sm font-semibold leading-5 text-slate-800 dark:text-slate-100">
          {children || valueOrDash(value)}
        </div>
      </div>
    </div>
  );
}

function CompactSection({ title, children }) {
  return (
    <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50/80 p-3 dark:border-slate-800 dark:bg-slate-900/50">
      <p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
        {title}
      </p>

      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {children}
      </div>
    </div>
  );
}

function NotesBlock({ adherent }) {
  const hasAdherentNote = hasValue(adherent?.notes_adherent);
  const hasTeacherNote = hasValue(adherent?.notes_enseignant);

  if (!hasAdherentNote && !hasTeacherNote) {
    return (
      <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900/50 dark:text-slate-400">
        Aucune note renseignée.
      </div>
    );
  }

  return (
    <details className="group rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/50">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-white text-slate-500 shadow-sm dark:bg-slate-950 dark:text-slate-300">
            <FaInfoCircle />
          </span>

          <div>
            <p className="text-sm font-bold text-slate-900 dark:text-white">
              Notes internes
            </p>

            <p className="text-xs text-slate-500 dark:text-slate-400">
              Notes adhérent et enseignant
            </p>
          </div>
        </div>

        <span className="text-xs font-bold text-slate-400 group-open:hidden">
          Voir
        </span>

        <span className="hidden text-xs font-bold text-slate-400 group-open:inline">
          Masquer
        </span>
      </summary>

      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
            Notes adhérent
          </p>

          <p className="mt-2 max-h-[180px] overflow-y-auto whitespace-pre-wrap pr-1 text-sm leading-6 text-slate-700 dark:text-slate-300">
            {adherent?.notes_adherent || "—"}
          </p>
        </div>

        <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
            Notes enseignant
          </p>

          <p className="mt-2 max-h-[180px] overflow-y-auto whitespace-pre-wrap pr-1 text-sm leading-6 text-slate-700 dark:text-slate-300">
            {adherent?.notes_enseignant || "—"}
          </p>
        </div>
      </div>
    </details>
  );
}

export default function IdentityPanel({ adherent }) {
  const age = computeAge(adherent?.date_naissance);

  const secondaryContactItems = useMemo(() => {
    return [
      {
        show: hasValue(adherent?.email_secondaire),
        icon: FaEnvelope,
        label: "Email secondaire",
        value: adherent?.email_secondaire,
      },
      {
        show: hasValue(adherent?.tel_secondaire),
        icon: FaPhoneAlt,
        label: "Téléphone secondaire",
        value: adherent?.tel_secondaire,
      },
    ].filter((item) => item.show);
  }, [adherent]);

  if (!adherent) {
    return (
      <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 p-5 text-center dark:border-slate-700 dark:bg-slate-900/60">
        <p className="text-sm font-bold text-slate-800 dark:text-slate-100">
          Aucun adhérent sélectionné
        </p>

        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Sélectionnez un adhérent pour afficher son identité.
        </p>
      </div>
    );
  }

  const address = formatAddress(adherent);

  return (
    <div className="space-y-3">
      <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
              Fiche identité
            </p>

            <h3 className="mt-1 truncate text-xl font-bold text-slate-950 dark:text-white">
              {getFullName(adherent)}
            </h3>

            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
              <span>{valueOrDash(adherent?.genre)}</span>
              <span className="text-slate-300">•</span>
              <span>
                {age !== null ? `${age} ans` : "Âge non précisé"}
              </span>
              <span className="text-slate-300">•</span>
              <span>{valueOrDash(adherent?.saison_active)}</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 dark:bg-slate-900 dark:text-slate-300">
              <FaIdCard />
              {valueOrDash(adherent?.licence_number)}
            </span>

            <span className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-2 text-xs font-bold text-orange-700 ring-1 ring-orange-200 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/20">
              <FaWeight />
              {formatWeight(adherent?.poids)}
            </span>
          </div>
        </div>
      </div>

      <CompactSection title="Informations principales">
        <CompactItem
          icon={FaBirthdayCake}
          label="Naissance"
          value={[
            formatDate(adherent?.date_naissance),
            adherent?.lieu_naissance,
          ]
            .filter(Boolean)
            .join(" · ")}
        />

        <CompactItem
          icon={FaUser}
          label="Représentant légal"
          value={adherent?.representant_legal}
        />

        <CompactItem
          icon={FaIdCard}
          label="Pass’Sport"
          value={adherent?.passsport_number}
        />
      </CompactSection>

      <CompactSection title="Coordonnées">
        <CompactItem
          icon={FaEnvelope}
          label="Email principal"
          value={adherent?.email}
        />

        <CompactItem
          icon={FaPhoneAlt}
          label="Téléphone principal"
          value={adherent?.tel}
        />

        {secondaryContactItems.map((item) => (
          <CompactItem
            key={item.label}
            icon={item.icon}
            label={item.label}
            value={item.value}
          />
        ))}

        <CompactItem icon={FaHome} label="Adresse" value={address} />
      </CompactSection>

      <CompactSection title="Autorisations & suivi">
        <CompactItem icon={FaCamera} label="Autorisation photo">
          <BooleanBadge value={adherent?.autorisation_photo} />
        </CompactItem>

        <CompactItem
          icon={FaInfoCircle}
          label="Créé le"
          value={formatDateTime(adherent?.createdAt)}
        />

        <CompactItem
          icon={FaInfoCircle}
          label="Mis à jour le"
          value={formatDateTime(adherent?.updatedAt)}
        />
      </CompactSection>

      <NotesBlock adherent={adherent} />
    </div>
  );
}